
import jsPDF from 'jspdf';
import { VorankuendigungCoordinator, VorankuendigungData, VorankuendigungParty } from '../../types';
import type { VorankPdfUnterschrift } from '../vorank/vorankUnterschriftTypes';
import { tagAusIso } from '../vorank/vorankAnfragePruefung';
import { NICHT_ANGEGEBEN } from './fehlendeAngabe';
import { winAnsiSicher } from './winAnsi';
import {
    contactLines,
    describeRole,
    filledCoordinators,
    filledParties,
    filledPersons,
    normalizeVorankuendigung,
} from '../vorankuendigungModel';

// ---------------------------------------------------------------------------
// Unterschriebene Fassung (docs/vorankuendigung-unterschrift-plan.md §7)
// ---------------------------------------------------------------------------

/** Kalendertag und Uhrzeit in Europe/Berlin — die Zone der Baustelle, nie die des Geräts. */
const BERLIN_DATUM = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric',
});
const BERLIN_UHRZEIT = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});

const alsZeitpunkt = (iso: string | null | undefined): Date | null => {
    const ms = Date.parse(iso || '');
    return Number.isFinite(ms) ? new Date(ms) : null;
};

/** ISO-Zeitpunkt → „28.10.2026“ (Berliner Kalendertag); '' bei ungültiger Angabe. */
export const vorankBerlinDatum = (iso: string | null | undefined): string => {
    const d = alsZeitpunkt(iso);
    return d ? BERLIN_DATUM.format(d) : '';
};

/** ISO-Zeitpunkt → „14:03“ (Berliner Uhrzeit); '' bei ungültiger Angabe. */
export const vorankBerlinUhrzeit = (iso: string | null | undefined): string => {
    const d = alsZeitpunkt(iso);
    return d ? BERLIN_UHRZEIT.format(d) : '';
};

/**
 * Der Ort aus „Ort, Datum“ (`locationDate`, Freitext): der Teil vor dem
 * ersten Komma. Ein Datum ohne Komma dahinter („Kiel den 21.08.2026“) fällt
 * weg; steht dort nur ein Datum, gibt es keinen Ort.
 */
export const ortAusOrtDatum = (ortDatum: string | null | undefined): string =>
    (ortDatum || '')
        .split(',')[0]
        .replace(/(^|\s)((den|am)\s+)?\d{1,2}\.\s?\d{1,2}\.(\s?\d{2}(\d{2})?)?$/i, '')
        .trim();

/**
 * Das Datum unter „Ort, Datum“: das eingetragene (E6, `YYYY-MM-DD` →
 * „27.10.2026“), sonst — Unterschrift von vor E6 oder unlesbar — der
 * Berliner Kalendertag des Zeitpunkts `am`.
 */
export const vorankOrtDatumTag = (u: Pick<VorankPdfUnterschrift, 'am' | 'datum'>): string =>
    tagAusIso(u.datum) || vorankBerlinDatum(u.am);

/**
 * Die graue Nachweiszeile unter den Unterschriften — immer der echte
 * Zeitpunkt, auch wenn „Ort, Datum“ ein anderes eingetragenes Datum trägt:
 * „Digital unterschrieben am 28.10.2026, 14:03 Uhr über Link …a1b2c3 (Fassung vom 27.10.2026)“.
 */
export const vorankNachweisZeile = (u: Pick<VorankPdfUnterschrift, 'am' | 'art' | 'kennung' | 'fassungVom'>): string => {
    const kennung = (u.kennung || '').trim().slice(-6);
    const fassung = vorankBerlinDatum(u.fassungVom);
    return `Digital unterschrieben am ${vorankBerlinDatum(u.am)}, ${vorankBerlinUhrzeit(u.am)} Uhr`
        + (kennung ? ` über Link …${kennung}` : '')
        + (fassung ? ` (Fassung vom ${fassung})` : '')
        + (u.art === 'bild' ? ' (Unterschrift als Bild hochgeladen)' : '');
};

/** Größtes Unterschriftsbild über der Linie (mm); das Seitenverhältnis bleibt. */
const UNTERSCHRIFT_MAX_BREITE = 55;
const UNTERSCHRIFT_MAX_HOEHE = 14;

export interface VorankPdfOptionen {
    /** Nur für die unterschriebene Fassung: Bild, Name, Ort/Datum und Nachweiszeile. */
    unterschrift?: VorankPdfUnterschrift | null;
}

/**
 * Vorankündigung gemäß BaustellV als PDF.
 *
 * Das Blatt ist bewusst kein starres Formular mehr: jede Zeile wächst mit ihrem
 * Inhalt, und wenn der Platz nicht reicht, bricht der Satz auf eine Folgeseite
 * um. Die Seitenzahlen ("Seite 2/3") stehen deshalb erst am Ende fest und
 * werden nachträglich auf alle Seiten gestempelt.
 *
 * Mit `opt.unterschrift` entsteht die digital unterschriebene Fassung: Bild
 * über der Unterschriftslinie, Name und „Ort, Datum“ der Unterschrift, darunter
 * eine graue Nachweiszeile. Ohne Option bleibt das Blatt genau wie zuvor.
 */
export const generateVorankuendigungPDF = (input: VorankuendigungData, opt?: VorankPdfOptionen): jsPDF => {
    const data = normalizeVorankuendigung(input);
    const unterschrift = opt?.unterschrift ?? null;

    const doc = new jsPDF({ format: 'a4', unit: 'mm' });
    const margin = 20;
    const pageWidth = 210;
    const contentWidth = pageWidth - margin * 2;
    const headerRightX = pageWidth - margin;

    /** Unterkante des Fließsatzes. Darunter liegen nur noch Fußzeile und Unterschriften. */
    const contentBottom = 258;
    /** Oberkante des Fließsatzes auf Folgeseiten (unter dem Kopf). */
    const contentTop = 38;

    const lineH = 4.2;
    const noteH = 3.6;

    let y = 0;

    const drawPageHeader = () => {
        doc.setFontSize(24);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text('Vorankündigung', headerRightX, 15, { align: 'right' });

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(0);
        // Platz für die Seitenzahl freihalten — sie wird erst am Ende gestempelt.
        doc.text('gemäß BaustellV', headerRightX - doc.getTextWidth('Seite 9/9') - 5, 21, { align: 'right' });
    };

    const newPage = () => {
        doc.addPage();
        drawPageHeader();
        y = contentTop;
    };

    /** Sorgt dafür, dass `needed` Millimeter noch auf die laufende Seite passen. */
    const ensure = (needed: number) => {
        if (y + needed > contentBottom) newPage();
    };

    const body = (size = 9) => {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(size);
        doc.setTextColor(0);
    };

    const bold = (size = 9) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(size);
        doc.setTextColor(0);
    };

    /** Zeilenweise setzen, damit ein Block mitten im Text umbrechen darf. */
    const drawFlow = (lines: string[], x: number, height = lineH) => {
        lines.forEach(line => {
            ensure(height);
            doc.text(line, x, y);
            y += height;
        });
    };

    const drawLabel = (label: string) => {
        bold();
        const lines = doc.splitTextToSize(label, contentWidth);
        ensure(lines.length * lineH + lineH);
        drawFlow(lines, margin);
        y += 1;
    };

    const drawRule = (gapAfter = 6) => {
        ensure(2);
        doc.setDrawColor(180);
        doc.setLineWidth(0.2);
        doc.line(margin, y, margin + contentWidth, y);
        y += gapAfter;
    };

    /**
     * Zweispaltige Zeile: links Anschrift, rechts Erreichbarkeit. Beide Spalten
     * wachsen unabhängig, gesetzt wird zeilenweise im Gleichschritt.
     */
    const drawTwoColumns = (leftLines: string[], rightLines: string[], minRows = 1) => {
        const leftX = margin + 2;
        const rightX = margin + contentWidth * 0.58;
        const leftWidth = contentWidth * 0.54;
        const rightWidth = contentWidth * 0.42;

        const left = leftLines.flatMap(l => doc.splitTextToSize(l, leftWidth) as string[]);
        const right = rightLines.flatMap(l => doc.splitTextToSize(l, rightWidth) as string[]);
        const rows = Math.max(left.length, right.length, minRows);

        // Einen Eintrag nicht mitten durchreissen: passt der ganze Block auf
        // eine Seite, wandert er als Ganzes auf die naechste.
        const blockHeight = rows * lineH;
        if (blockHeight <= contentBottom - contentTop) ensure(blockHeight);

        for (let i = 0; i < rows; i++) {
            ensure(lineH);
            if (left[i]) doc.text(left[i], leftX, y);
            if (right[i]) doc.text(right[i], rightX, y);
            y += lineH;
        }
    };

    /** Beschriftung und Wert in einer Zeile — passt der Wert nicht, rutscht er darunter. */
    const drawInlineRow = (label: string, value: string) => {
        bold();
        const labelLines = doc.splitTextToSize(label, contentWidth) as string[];
        ensure(labelLines.length * lineH + 3);
        labelLines.slice(0, -1).forEach(line => {
            doc.text(line, margin, y);
            y += lineH;
        });
        const lastLabel = labelLines[labelLines.length - 1] || '';
        doc.text(lastLabel, margin, y);
        const labelWidth = doc.getTextWidth(lastLabel);

        // Eine fehlende Angabe wird benannt, nicht mit „-" gefuellt: bei der
        // Zahl der Beschaeftigten laese das Amt den Strich als „keine" — ein
        // anderer Sachverhalt als „nicht ausgefuellt". Kursiv und grau wie die
        // Erlaeuterungen, damit die Luecke nicht wie ein eingetragener Wert
        // aussieht.
        const wert = (value || '').trim();
        const text = wert || NICHT_ANGEGEBEN;
        body();
        if (!wert) {
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(100);
        }
        const free = contentWidth - labelWidth - 5;
        const valueLines = doc.splitTextToSize(text, Math.max(free, 20)) as string[];

        if (valueLines.length === 1 && doc.getTextWidth(text) <= free) {
            doc.text(text, margin + labelWidth + 5, y);
            y += lineH;
        } else {
            y += lineH;
            drawFlow(doc.splitTextToSize(text, contentWidth - 2) as string[], margin + 2);
        }
        doc.setTextColor(0);
        y += 1;
        drawRule();
    };

    /** Beschriftung, darunter der (beliebig lange) Inhalt, darunter die Linie. */
    const drawField = (label: string, content: string) => {
        drawLabel(label);
        body();
        const text = (content || '').trim();
        if (text) {
            drawFlow(doc.splitTextToSize(text, contentWidth - 2) as string[], margin + 2);
        } else {
            ensure(lineH);
            y += lineH;
        }
        y += 1;
        drawRule();
    };

    // -----------------------------------------------------------------------
    // SEITE 1 — Kopf, Absenderkasten, Empfänger
    // -----------------------------------------------------------------------
    drawPageHeader();

    const senderBoxY = 32;
    const senderBoxWidth = 85;
    const senderInner = senderBoxWidth - 4;

    body(10);
    const senderLines = (data.sender || '').split('\n');
    const senderName = doc.splitTextToSize(senderLines[0] || '', senderInner) as string[];
    const senderRest = doc.splitTextToSize(senderLines.slice(1).join('\n'), senderInner) as string[];
    // Der Kasten wächst mit dem Absender, bleibt aber mindestens formularhoch.
    const senderBoxHeight = Math.max(40, 12 + (senderName.length + senderRest.length) * 5);

    doc.setDrawColor(150);
    doc.setLineWidth(0.3);
    doc.rect(margin, senderBoxY, senderBoxWidth, senderBoxHeight);

    doc.setFontSize(7);
    doc.setTextColor(100);
    doc.text('Absender (Name, Anschrift, E-Mail Adresse)', margin + 2, senderBoxY + 4);
    doc.setTextColor(0);

    let senderY = senderBoxY + 10;
    if (senderName.length) {
        bold(10);
        doc.text(senderName, margin + 2, senderY);
        senderY += senderName.length * 5;
    }
    if (senderRest.length) {
        body(10);
        doc.text(senderRest, margin + 2, senderY);
        senderY += senderRest.length * 5;
    }

    const recipientX = 115;
    let recipientY = senderBoxY + 5;
    const recipientParts = (data.recipient || '').split('\n');

    bold(11);
    const recipientName = doc.splitTextToSize(recipientParts[0] || '', 75) as string[];
    doc.text(recipientName, recipientX, recipientY);
    recipientY += recipientName.length * 5;

    const recipientRest = recipientParts.slice(1).join('\n');
    if (recipientRest.trim()) {
        body(10);
        const rest = doc.splitTextToSize(recipientRest, 75) as string[];
        doc.text(rest, recipientX, recipientY);
        recipientY += rest.length * 5;
    }

    y = Math.max(senderBoxY + senderBoxHeight, recipientY) + 10;

    // -----------------------------------------------------------------------
    // Punkte 1 bis 5
    // -----------------------------------------------------------------------
    drawField('1. Bezeichnung und Ort der Baustelle (Straße/Nr., PLZ/Ort):', data.siteAddress);

    // --- Punkt 2: Firma links, Erreichbarkeit rechts, darunter die Personen ---
    drawLabel('2. Name und Anschrift des Bauherrn inkl. natürliche Person (Telefon, E-Mail):');
    body();
    drawTwoColumns(
        [data.clientName || '', ...(data.clientAddress || '').split(/,\s*|\n/)].filter(l => l.trim()),
        contactLines({ phone: data.clientPhone, email: data.clientEmail }),
        2,
    );

    const clientPersons = filledPersons(data.clientPersons);
    if (clientPersons.length) {
        y += 1;
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(120);
        ensure(noteH);
        doc.text('Natürliche Person(en):', margin + 2, y);
        y += noteH + 0.5;
        doc.setTextColor(0);
        body();
        clientPersons.forEach(person => {
            drawTwoColumns([describeRole(person.role, person.name)], contactLines(person));
        });
    }
    y += 1;
    drawRule();

    drawField('3. Art und Bezeichnung des Bau- und/oder Abrissvorhabens:', data.projectDescription);

    // --- Punkt 4: beliebig viele verantwortliche Dritte ---
    drawLabel('4. Name und Anschrift des verantwortlichen Dritten (gemäß § 4 BauStellV):');
    const parties: VorankuendigungParty[] = filledParties(data.responsibleThirdParties);
    body();
    if (parties.length) {
        parties.forEach((party, index) => {
            if (index > 0) y += 2;
            drawTwoColumns(
                [describeRole(party.role, party.name), ...(party.address || '').split('\n')].filter(l => l.trim()),
                contactLines(party),
            );
        });
    } else {
        ensure(lineH);
        y += lineH;
    }
    y += 1;
    drawRule();

    // --- Punkt 5: Koordinatoren, je Unterpunkt beliebig viele Personen ---
    drawLabel('5. Koordinator/en mit Anschrift, Telefon und E-Mail (gemäß § 3 BauStellV):');

    const drawCoordinatorBlock = (subLabel: string, list: VorankuendigungCoordinator[]) => {
        bold();
        // Der Unterpunkt darf nicht allein am Seitenfuss stehen.
        ensure(lineH * 5);
        doc.text(subLabel, margin + 5, y);
        y += lineH + 1;

        body();
        if (!list.length) {
            ensure(lineH * 2);
            y += lineH * 2;
            return;
        }

        list.forEach((entry, index) => {
            if (index > 0) y += 2;
            drawTwoColumns(
                (entry.company || '').split('\n').filter(l => l.trim()),
                [describeRole(entry.role, entry.name), ...contactLines(entry)].filter(l => l.trim()),
            );
        });
    };

    drawCoordinatorBlock('a) für die Planung der Ausführung:', filledCoordinators(data.planningCoordinators));
    y += 1;
    drawRule();

    drawCoordinatorBlock('b) für die Ausführung des Bauvorhabens:', filledCoordinators(data.executionCoordinators));
    y += 1;
    drawRule(8);

    // -----------------------------------------------------------------------
    // SEITE 2 — Punkte 6 bis 12
    // -----------------------------------------------------------------------
    // Im Regelfall beginnen die Punkte 6 ff. auf einem frischen Blatt. Ist die
    // Vorderseite dagegen bereits uebergelaufen, fliesst der Rest weiter, statt
    // eine halbleere Seite dazwischenzuschieben.
    if (contentBottom - y < 90) newPage();

    // Punkt 6: Zeitraum in einer Zeile, notfalls unter der Beschriftung.
    bold();
    const dateLabel = '6. Voraussichtlicher Beginn und geplantes Ende der Arbeiten:';
    ensure(lineH * 2);
    doc.text(dateLabel, margin, y);
    const dateLabelWidth = doc.getTextWidth(dateLabel);

    const dateStart = (data.startDate || '').trim();
    const dateEnd = (data.endDate || '').trim();
    body();
    const dateSpanWidth =
        doc.getTextWidth('von ') + doc.getTextWidth('bis ') + 10 + doc.getTextWidth(dateStart) + doc.getTextWidth(dateEnd);

    let dateX = margin + dateLabelWidth + 5;
    if (dateLabelWidth + 5 + dateSpanWidth > contentWidth) {
        y += lineH;
        dateX = margin + 2;
    }
    doc.text('von', dateX, y);
    dateX += doc.getTextWidth('von') + 3;
    bold();
    doc.text(dateStart, dateX, y);
    dateX += Math.max(doc.getTextWidth(dateStart), 20) + 5;
    body();
    doc.text('bis', dateX, y);
    dateX += doc.getTextWidth('bis') + 3;
    bold();
    doc.text(dateEnd, dateX, y);
    y += lineH + 1;
    drawRule();

    drawInlineRow('7. Voraussichtliche Zahl der gleichzeitig auf der Baustelle Beschäftigten:', data.maxWorkers);
    drawInlineRow('8. Voraussichtliche Zahl der Arbeitgebenden:', data.numEmployers);
    drawInlineRow('9. Voraussichtliche Zahl der Unternehmer/innen ohne Beschäftigte:', data.numContractors);

    // Punkt 10: zweispaltige Liste, wächst mit der Zahl der Einträge.
    y += 2;
    drawLabel('10. Bereits ausgewählte Arbeitgebende:');

    const employers = (data.employersList || []).map(e => (e || '').trim());
    // Mindestens zwölf Zeilen, damit das Blatt weiterhin als Formular ausfüllbar bleibt.
    const employerRows = Math.max(12, employers.length);
    const colW = contentWidth / 2;

    for (let row = 0; row < Math.ceil(employerRows / 2); row++) {
        const cells = [employers[row * 2], employers[row * 2 + 1]];
        const wrapped = cells.map(cell => doc.splitTextToSize(cell || '', colW - 14) as string[]);
        const height = Math.max(wrapped[0].length, wrapped[1].length, 1) * lineH + 2;
        ensure(height + 2);

        wrapped.forEach((lines, col) => {
            const index = row * 2 + col;
            if (index >= employerRows) return;
            const x = margin + col * colW;

            body(8);
            doc.setTextColor(100);
            doc.text(`${index + 1}.`, x, y);
            doc.setTextColor(0);

            body(9);
            lines.forEach((line, i) => doc.text(line, x + 8, y + i * lineH));

            doc.setDrawColor(200);
            doc.line(x, y + height - 3, x + colW - 5, y + height - 3);
        });
        y += height;
    }
    y += 6;

    // Punkt 11
    bold();
    drawLabel('11. Auf der Baustelle werden besonders gefährliche Arbeiten i. S. v. § 2 (3) BauStellV durchgeführt:');

    ensure(lineH * 2);
    const drawCheck = (label: string, checked: boolean, xOffset: number) => {
        doc.setDrawColor(0);
        doc.setLineWidth(0.2);
        doc.rect(margin + xOffset, y - 3, 4, 4);
        if (checked) {
            doc.line(margin + xOffset, y - 3, margin + xOffset + 4, y + 1);
            doc.line(margin + xOffset + 4, y - 3, margin + xOffset, y + 1);
        }
        doc.text(label, margin + xOffset + 6, y);
    };

    body();
    drawCheck('nein', !data.hasDangerousWork, 0);
    drawCheck('ja - wenn ja, welche?', data.hasDangerousWork, 20);
    y += lineH + 1;

    if (data.hasDangerousWork) {
        body();
        drawFlow(doc.splitTextToSize(data.dangerousWorkDescription || '', contentWidth) as string[], margin);
    } else {
        y += lineH;
    }
    y += 1;
    drawRule(4);

    doc.setFontSize(7);
    doc.setTextColor(100);
    ensure(noteH);
    doc.text('(Arbeiten mit Versinken, Verschüttetwerden, Absturz > 7m, Explosivstoffe, Strahlung, etc.)', margin, y);
    doc.setTextColor(0);
    y += 8;

    if (data.hasSiteManager) {
        drawInlineRow('12. Verantwortliche Bauleitung nach LBO / HBauO (Name, Telefon):', data.siteManager);
    }

    // -----------------------------------------------------------------------
    // Unterschriften + Schlusssatz auf der letzten Seite
    // -----------------------------------------------------------------------
    if (y > 250) newPage();
    y = 262;

    const sigW = contentWidth / 3;
    doc.setDrawColor(0);
    doc.setLineWidth(0.2);

    /**
     * Nur für die unterschriebene Fassung: Text in 9 pt, so weit verkleinert
     * (bis 6,5 pt), dass er auf seine Linie passt und nicht in die Nachbarspalte
     * läuft. Name und Ort kommen dort aus der öffentlichen Seite — Freitext,
     * daher CP1252-sicher gemacht.
     */
    const setzeAufLinie = (text: string, x: number, maxBreite: number) => {
        let groesse = 9;
        doc.setFontSize(groesse);
        while (groesse > 6.5 && doc.getTextWidth(text) > maxBreite) {
            groesse -= 0.5;
            doc.setFontSize(groesse);
        }
        doc.text(text, x, y);
    };

    body(9);
    if (unterschrift) {
        const ort = winAnsiSicher(ortAusOrtDatum(data.locationDate));
        const datum = vorankOrtDatumTag(unterschrift);
        setzeAufLinie([ort, datum].filter(Boolean).join(', '), margin, sigW - 5);
    } else {
        doc.text(data.locationDate || '', margin, y);
    }
    doc.line(margin, y + 1, margin + sigW - 5, y + 1);
    doc.setFontSize(7);
    doc.text('Ort, Datum', margin, y + 5);

    doc.setFontSize(9);
    if (unterschrift) {
        const name = winAnsiSicher((unterschrift.name || '').trim());
        const funktion = winAnsiSicher((unterschrift.funktion || '').trim());
        const mitFunktion = funktion ? `${name}, ${funktion}` : name;
        // Die Funktion nur, wenn sie in voller Größe mit auf die Linie passt —
        // sonst lieber der Name allein und gut lesbar.
        const passt = doc.getTextWidth(mitFunktion) <= sigW - 5;
        setzeAufLinie(passt ? mitFunktion : name, margin + sigW, sigW - 5);
    } else {
        doc.text(data.signerName || '', margin + sigW, y);
    }
    doc.line(margin + sigW, y + 1, margin + sigW * 2 - 5, y + 1);
    doc.setFontSize(7);
    doc.text('Name', margin + sigW, y + 5);

    doc.line(margin + sigW * 2, y + 1, margin + contentWidth, y + 1);
    doc.text('Unterschrift des Bauherrn / Verantwortlichen', margin + sigW * 2, y + 5);

    if (unterschrift) {
        // Bild über der rechten Linie, höchstens 55 × 14 mm, Seitenverhältnis
        // bleibt; waagerecht mittig in der Spalte. Die Unterkante liegt einen
        // halben Millimeter unter der Linie — der Zug „steht“ auf ihr, wie mit
        // dem Stift (das PNG ist transparent, die Linie bleibt sichtbar).
        // Ein kaputtes Bild bricht ab: eine „unterschriebene“ Fassung ohne
        // Unterschrift darf nicht entstehen.
        let bildB: number;
        let bildH: number;
        try {
            const eigenschaften = doc.getImageProperties(unterschrift.bild);
            const faktor = Math.min(
                UNTERSCHRIFT_MAX_BREITE / eigenschaften.width,
                UNTERSCHRIFT_MAX_HOEHE / eigenschaften.height,
            );
            bildB = eigenschaften.width * faktor;
            bildH = eigenschaften.height * faktor;
        } catch {
            throw new Error('Das Bild der Unterschrift lässt sich nicht lesen — die unterschriebene Vorankündigung kann nicht entstehen.');
        }
        if (!(bildB > 0 && bildH > 0)) {
            throw new Error('Das Bild der Unterschrift ist leer — die unterschriebene Vorankündigung kann nicht entstehen.');
        }
        const spalteX = margin + sigW * 2;
        const spalteB = contentWidth - sigW * 2;
        // Vertrag: PNG. Ein JPEG (etwa aus einem älteren Stand) geht trotzdem durch.
        const format = /^data:image\/jpe?g/i.test(unterschrift.bild) ? 'JPEG' : 'PNG';
        doc.addImage(unterschrift.bild, format, spalteX + (spalteB - bildB) / 2, y + 1.5 - bildH, bildB, bildH);

        // Nachweis in 6 pt grau unter den Beschriftungen (y + 5). Der Schlusssatz
        // steht bei 280 (10 pt fett, Oberkante ≈ 277,5) — zwei Zeilen hätten
        // dazwischen noch Platz.
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(6);
        doc.setTextColor(110);
        const nachweis = doc.splitTextToSize(vorankNachweisZeile(unterschrift), contentWidth) as string[];
        nachweis.slice(0, 2).forEach((zeile, i) => doc.text(zeile, margin, y + 9.5 + i * 2.6));
        doc.setTextColor(0);
    }

    doc.setFontSize(10);
    bold(10);
    doc.text('Die Vorankündigung ist sichtbar auf der Baustelle auszuhängen.', margin, 280);
    doc.setLineWidth(0.5);
    doc.line(margin, 282, margin + contentWidth, 282);

    // -----------------------------------------------------------------------
    // Seitenzahlen und Blätterhinweis — erst jetzt steht die Seitenzahl fest.
    // -----------------------------------------------------------------------
    const total = doc.getNumberOfPages();
    for (let page = 1; page <= total; page++) {
        doc.setPage(page);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(150);
        doc.text(`Seite ${page}/${total}`, headerRightX, 21, { align: 'right' });
        doc.setTextColor(0);

        if (page < total) {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.text('Bitte beachten Sie auch die Folgeseite', headerRightX, 280, { align: 'right' });
            doc.setDrawColor(0);
            doc.setLineWidth(0.2);
            doc.line(headerRightX - 55, 281, headerRightX, 281);
        }
    }

    return doc;
};
